import { Component, Vue, Watch } from "vue-property-decorator";
import Broadcaster, {
  BroadcastersCatalog,
  BroadcastersCollection,
} from "@/models/SpecialInitiatives/Broadcaster";
import WindowEvent from "@/decorators/WindowEvent";

type Kind = "TV" | "RADIO";

interface SpecialInitiativesColumn {
  value: string;
  label: string;
  isHighlight?: boolean;
}

interface SpecialInitiativesRow {
  subject: string;
  broadcaster?: string;
  kind: Kind | "";
  data: { [key: string]: { value: number; quote: string } };
  children: SpecialInitiativesRow[];
  isTotal: boolean;
  logoUrls?: string[];
}

type CollectionData = {
  col: string;
  value: number;
  quote: number;
  logoUrls?: string[];
  companyIds: Set<string>;
};
type SubjectCollection = { [key: string]: CollectionData[] };
type ParentRecord = {
  data: CollectionData[];
  subjects: SubjectCollection;
  mediaType: Kind;
};
type ParentCollection = { [key: string]: ParentRecord };

export interface SpecialInitiativesIData {
  [key: string]: Broadcaster[];
}

export type SubjectColumn = "sales_agencies" | "editors" | "market_segments";

export interface SpecialInitiativesIColumn {
  name: string;
  label: string;
}

@Component
export default class MarketMixin extends Vue {
  columns: SpecialInitiativesColumn[] = [];
  rows: SpecialInitiativesRow[] = [];
  sortColumn = "";
  sortDirection: "ASC" | "DESC" | null = null;

  value: SpecialInitiativesIData | null = null;
  _broadcasters: BroadcastersCatalog = {};
  _subject: SubjectColumn = "sales_agencies";
  _unitOfMeasurement = "";
  _loading = false;
  _weekRanges: SpecialInitiativesIColumn[] = [];

  buildSpecialInitiativesColumnsAndRows(
    loading: boolean,
    exclusivity: string,
    subject: SubjectColumn,
    broadcasters: BroadcastersCatalog,
    weekRanges: SpecialInitiativesIColumn[],
    unitOfMeasurement: string
  ) {
    this._loading = loading;
    this._subject = subject;
    this._broadcasters = broadcasters;
    this._unitOfMeasurement = unitOfMeasurement;
    this._weekRanges = weekRanges;
    this.value = this.broadcastersToTableData(exclusivity);
    this.columns = this.loadColumns();
    this.convertValueToRows();
  }

  broadcastersToTableData(exclusivity: string): SpecialInitiativesIData {
    const result: SpecialInitiativesIData = {};
    for (const weekRange in this._broadcasters) {
      result[weekRange] = this.broadcastersFromExclusivity(
        this._broadcasters[weekRange],
        exclusivity
      );
    }
    return result;
  }

  broadcastersFromExclusivity(
    broadcasters: BroadcastersCollection,
    exclusivity: string
  ) {
    if (exclusivity === "all") {
      // This reduces a list with many instances of the same broadcaster,
      // with a different exclusivity, to a list with only one instance per broadcaster
      // with detections summed
      const all = broadcasters.broadcastersAll;
      const grouped: { [key: string]: Broadcaster[] } = {};
      all.forEach((b) => {
        const key = b.name;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        // Object assign to create a fresh instance of the broadcaster at each call
        grouped[key].push(Object.assign(new Broadcaster(b.toJSON()), b));
      });
      //Sum the detections from the same broadcaster
      const reduced = Object.keys(grouped).map((key) => {
        const totalSeconds = grouped[key].reduce(
          (acc, b) => acc + b.secondsCount,
          0
        );
        const totalDetections = grouped[key].reduce(
          (acc, b) => acc + b.detectionsCount,
          0
        );

        return Object.assign(grouped[key][0], {
          secondsCount: totalSeconds,
          detectionsCount: totalDetections,
        });
      });
      return reduced;
    } else if (exclusivity === "my_exclusivity_type") {
      return broadcasters.broadcastersMine;
    } else if (exclusivity === "others_exclusivity_type") {
      return broadcasters.broadcastersOthers;
    } else if (exclusivity === "shared_exclusivity_type") {
      return broadcasters.broadcastersShared;
    } else {
      return [];
    }
  }

  convertValueToRows() {
    const parents = this.convertBroadcastersToParentsCollection();
    const tvTotals: CollectionData[] = [];
    const radioTotals: CollectionData[] = [];
    const totals = this.computeParentTotals(parents, tvTotals, radioTotals);
    this.convertParentsToRow(parents, totals, tvTotals, radioTotals);
  }

  dataColumn(columnName: string) {
    return columnName.startsWith("col");
  }

  setSort(column: SpecialInitiativesColumn) {
    if (this.sortColumn !== column.value) {
      this.sortColumn = column.value;
      this.sortDirection = "ASC";
    } else {
      switch (this.sortDirection) {
        case "ASC":
          this.sortDirection = "DESC";
          break;
        case "DESC":
          this.sortDirection = null;
          this.sortColumn = "";
          break;
        default:
          this.sortColumn = "ASC";
          break;
      }
    }
    if (this.sortDirection !== null) {
      this.rows.sort(this.sort.bind(this));
    } else {
      this.convertValueToRows();
    }
  }

  get dataColumns() {
    return this.columns.filter((c) => this.dataColumn(c.value));
  }

  private convertParentsToRow(
    parents: ParentCollection,
    totals: CollectionData[],
    tvTotals: CollectionData[],
    radioTotals: CollectionData[]
  ) {
    this.rows = [];
    for (const subject in parents) {
      const parent = parents[subject];
      const rowData: { [key: string]: { value: number; quote: string } } = {};
      this.dataColumns.forEach((column) => {
        const weekRange = column.value.split("-")[1];
        const total = totals.find((e) => e.col === weekRange) || {
          weekRange: "",
          value: 1,
          quote: 1,
        };
        const element = parent.data.find((e) => e.col === weekRange) || {
          weekRange,
          value: 0,
          quote: 0,
        };
        rowData[column.value] = {
          value: element.value,
          quote: Number((element.value / total.value) * 100).toFixed(0),
        };
      });
      const row: SpecialInitiativesRow = {
        subject,
        data: rowData,
        isTotal: false,
        kind: parent.mediaType,
        children: this.convertChildrenToRows(parent, totals),
      };
      this.rows.push(row);
    }
    if (tvTotals.length > 0) {
      this.rows.push(
        this.CollectionDataListToRow(tvTotals, "TOTALE TV", "TV", true)
      );
    }
    if (radioTotals.length > 0) {
      this.rows.push(
        this.CollectionDataListToRow(radioTotals, "TOTALE RADIO", "RADIO", true)
      );
    }
    this.rows.push(this.CollectionDataListToRow(totals, "TOTALE", "", true));
    this.rows.sort(this.sort.bind(this));
  }

  private CollectionDataListToRow(
    list: CollectionData[],
    name: string,
    kind: Kind | "",
    isTotal: boolean
  ) {
    const rowData: { [key: string]: { value: number; quote: string } } = {};
    this.dataColumns.forEach((column) => {
      const weekRange = column.value.split("-")[1];
      const element = list.find((e) => e.col === weekRange) || {
        weekRange,
        value: 0,
        quote: 0,
      };
      rowData[column.value] = {
        value: element.value,
        quote: "100",
      };
    });
    const row: SpecialInitiativesRow = {
      subject: name,
      data: rowData,
      isTotal,
      children: [],
      kind,
    };
    return row;
  }

  private sortByColumn(
    a: SpecialInitiativesRow,
    b: SpecialInitiativesRow,
    columnName: string,
    direction: "ASC" | "DESC"
  ): (a: SpecialInitiativesRow, b: SpecialInitiativesRow) => -1 | 0 | 1 {
    return (a: SpecialInitiativesRow, b: SpecialInitiativesRow) => {
      let result: -1 | 0 | 1 = 0;
      if (columnName == "subject") {
        if (a.subject < b.subject) {
          result = direction === "ASC" ? -1 : 1;
        } else if (b.subject < a.subject) {
          result = direction === "ASC" ? 1 : -1;
        }
      } else {
        if (a.data[columnName] === undefined) {
          result = direction === "ASC" ? -1 : 1;
        } else if (b.data[columnName] === undefined) {
          result = direction === "ASC" ? 1 : -1;
        } else if (a.data[columnName].value < b.data[columnName].value) {
          result = direction === "ASC" ? -1 : 1;
        } else {
          result = direction === "ASC" ? 1 : -1;
        }
      }
      return result;
    };
  }

  private sort(a: SpecialInitiativesRow, b: SpecialInitiativesRow): -1 | 0 | 1 {
    if (this.sortDirection !== null) {
      return this.baseSort(
        a,
        b,
        this.sortByColumn(a, b, this.sortColumn, this.sortDirection).bind(this)
      );
    }
    return this.baseSort(a, b);
  }

  private baseSort(
    a: SpecialInitiativesRow,
    b: SpecialInitiativesRow,
    otherCondition?: (
      a: SpecialInitiativesRow,
      b: SpecialInitiativesRow
    ) => -1 | 0 | 1
  ): -1 | 0 | 1 {
    let result: -1 | 0 | 1 = 0;
    if (a.isTotal && a.kind == "") {
      return 1;
    }
    if (b.isTotal && b.kind == "") {
      return -1;
    }
    if (a.kind == "TV" && b.kind == "RADIO") {
      result = -1;
    } else if (a.kind == "RADIO" && b.kind == "TV") {
      result = 1;
    }
    if (a.kind == b.kind) {
      if (a.isTotal) {
        result = 1;
      } else if (b.isTotal) {
        result = -1;
      } else {
        if (otherCondition) {
          result = otherCondition(a, b);
        }
      }
    }
    if (otherCondition) {
      a.children.sort(otherCondition);
      b.children.sort(otherCondition);
    }
    return result;
  }

  private convertChildrenToRows(
    parent: ParentRecord,
    databaseTotals: CollectionData[]
  ): SpecialInitiativesRow[] {
    const rows: SpecialInitiativesRow[] = [];
    for (const key in parent.subjects) {
      const totals =
        this._subject === "market_segments" ? parent.data : databaseTotals;
      const subject = parent.subjects[key];
      const rowData: { [key: string]: { value: number; quote: string } } = {};
      this.dataColumns.forEach((column) => {
        const weekRange = column.value.split("-")[1];
        const total = totals.find((e) => e.col === weekRange) || {
          weekRange: "",
          value: 1,
          quote: 1,
        };
        const element = subject
          .filter((e) => e.col === weekRange)
          .reduce(
            (acc, element) => {
              acc.weekRange = element.col;
              acc.value += element.value;
              return acc;
            },
            { weekRange, value: 0, quote: 0 }
          );
        rowData[column.value] = {
          value: element.value,
          quote: Number((element.value / total.value) * 100).toFixed(0),
        };
      });
      const row: SpecialInitiativesRow = {
        subject: key,
        data: rowData,
        isTotal: false,
        kind: parent.mediaType,
        children: [],
        logoUrls: subject[0].logoUrls,
      };
      rows.push(row);
    }
    return rows;
  }

  private computeParentTotals(
    parents: ParentCollection,
    tvTotals: CollectionData[],
    radioTotals: CollectionData[]
  ): CollectionData[] {
    const totals: CollectionData[] = [];
    for (const subject in parents) {
      const parent = parents[subject];
      parent.data = parent.data.reduce(
        (acc: CollectionData[], element: CollectionData) => {
          this.updateTotal(acc, element);
          if (parent.mediaType === "TV") {
            this.updateTotal(tvTotals, element);
          } else if (parent.mediaType === "RADIO") {
            this.updateTotal(radioTotals, element);
          }
          this.updateTotal(totals, element);
          return acc;
        },
        []
      );
    }
    return totals;
  }

  private updateTotal(totals: CollectionData[], element: CollectionData) {
    const total = totals.find((e) => e.col == element.col);
    if (!total) {
      if (this._unitOfMeasurement != "clients_number") {
        totals.push(Object.assign({}, element));
      } else {
        totals.push(
          Object.assign({}, element, { value: element.companyIds.size })
        );
      }
    } else {
      if (this._unitOfMeasurement != "clients_number") {
        total.value += element.value;
      } else {
        element.companyIds.forEach((companyId) => {
          total.companyIds.add(companyId);
        });
        total.value = total.companyIds.size;
      }
    }
  }

  private convertBroadcastersToParentsCollection(): ParentCollection {
    const parents: ParentCollection = {};
    for (const weekRange in this.value) {
      const records = this.value[weekRange];
      for (const record of records) {
        const key = this.detectionKey(record);
        const parent = parents[key] || {
          data: [],
          subjects: {},
          mediaType: record.mediaType,
        };
        const subject = parent.subjects[record.brandName] || [];
        this.updateSubjectAndParentData(subject, parent, record, weekRange);
        parent.subjects[record.brandName] = subject;
        parents[key] = parent;
      }
    }
    return parents;
  }

  private updateSubjectAndParentData(
    subject: CollectionData[],
    parent: ParentRecord,
    record: Broadcaster,
    col: string
  ) {
    let value = 0;
    const companyIds = new Set<string>();
    if (
      this._unitOfMeasurement === "seconds" ||
      this._unitOfMeasurement === "quoted_seconds"
    ) {
      value = record.secondsCount;
    }
    if (
      this._unitOfMeasurement === "spots" ||
      this._unitOfMeasurement === "quoted_spots"
    ) {
      value = record.detectionsCount;
    }
    if (this._unitOfMeasurement === "clients_number") {
      value = record.companiesCount;
      record.companyIds.split(",").forEach((companyId) => {
        companyIds.add(companyId);
      });
    }

    const data = {
      col,
      value,
      companyIds,
      quote: 0,
      logoUrls: record.logoUrls,
    };
    subject.push(data);
    parent.data.push(data);
  }

  private detectionKey(record: Broadcaster): string {
    if (this._subject === "sales_agencies") {
      return `${record.currentAgency}`;
    } else if (this._subject === "editors") {
      return `${record.publisher}`;
    }
    return `${record.marketSegment}`;
  }

  loadColumns(): SpecialInitiativesColumn[] {
    if (this._loading) {
      return [];
    }
    const columns: SpecialInitiativesColumn[] = [];
    switch (this._subject) {
      case "sales_agencies":
        columns.push({
          value: "subject",
          label: "Concessionarie",
          isHighlight: false,
        });
        break;
      case "editors":
        columns.push({
          value: "subject",
          label: "Editori",
          isHighlight: false,
        });
        break;
      case "market_segments":
        columns.push({
          value: "subject",
          label: "Segmenti di mercato",
          isHighlight: false,
        });
        break;
    }
    this._weekRanges.forEach((weekRange) => {
      columns.push({
        value: weekRange.name,
        label: weekRange.label,
        isHighlight: false,
      });
    });
    return columns;
  }
}
